import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiAward, FiBriefcase, FiStar, FiBookOpen, FiTrendingUp } = FiIcons;

const InstructorSection = ({ onBooking }) => {
  const instructors = [
    {
      name: "อาจารย์รวิ อัญญากาญจน์",
      title: "นักโหราศาสตร์จีน ประสบการณ์ 18 ปี",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394087849-blob",
      specialties: [
        "ผู้ให้คำปรึกษาเชิงกลยุทธ์ด้านธุรกิจและการลงทุน",
        "ผสานศาสตร์โบราณสู่การบริหารสมัยใหม่",
        "เชี่ยวชาญด้านการวิเคราะห์ดวงชะตาเพื่อการเงิน"
      ],
      icon: FiBriefcase,
      gradient: "from-blue-500 to-indigo-600",
      borderColor: "border-blue-500/30"
    },
    {
      name: "นักพรตเฮอซานเฉิง",
      title: "ผู้ชำนาญด้านพิธีกรรมเสริมดวงชะตา",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394018933-20231003_224303.jpg",
      specialties: [
        "ผู้เชียวชาญด้านการเงินและความมั่งคั่ง",
        "ประสบการณ์ด้านวิชาโหราศาสตร์จีนโบราณ",
        "ผู้รู้เรื่องพิธีกรรมลึกลับโบราณ"
      ],
      icon: FiStar,
      gradient: "from-purple-500 to-indigo-600",
      borderColor: "border-purple-500/30"
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 relative overflow-hidden">
      {/* Enhanced Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395134810-20240213_085721.jpg" 
          alt="Background" 
          className="absolute top-0 left-0 w-1/2 h-full object-cover rounded-r-3xl" 
        />
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395147479-blob" 
          alt="Background" 
          className="absolute bottom-0 right-0 w-1/2 h-2/3 object-cover rounded-tl-3xl" 
        />
        <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full blur-3xl opacity-40"></div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ผู้สอนที่มีประสบการณ์จริง
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-4">
            การรวมตัวครั้งพิเศษของผู้เชี่ยวชาญสองสาขา
            <br />
            <span className="text-amber-400 font-semibold">ทฤษฎีและปฏิบัติ</span> ในหลักสูตรเดียว
          </p>
          <div className="bg-slate-700/40 backdrop-blur-md rounded-lg p-4 border border-slate-600/30 max-w-2xl mx-auto">
            <p className="text-slate-300 font-medium text-lg">
              📅 วันเรียน: 12 กรกฎาคม 2568 | ⏰ เวลา: 09.00-17.00 น.<br />
              📍 S Ratchada Leisure Hotel ห้อง Galleria ชั้น 2
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {instructors.map((instructor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`bg-gradient-to-br from-slate-800/80 to-slate-700/80 backdrop-blur-sm rounded-2xl p-8 border ${instructor.borderColor} hover:border-opacity-80 transition-all duration-300 hover:transform hover:scale-105 shadow-2xl`}
            >
              <div className="text-center mb-6">
                {/* Instructor Photo */}
                <div className="relative mb-6">
                  <div className={`w-32 h-32 mx-auto rounded-full overflow-hidden border-4 ${instructor.borderColor.replace('/30', '')} shadow-2xl`}>
                    <img 
                      src={instructor.image} 
                      alt={instructor.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  {/* Decorative ring */}
                  <div className={`absolute inset-0 w-32 h-32 mx-auto rounded-full border-2 ${instructor.borderColor.replace('/30', '/50')} animate-pulse`}></div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">
                  {instructor.name}
                </h3>
                <p className="text-amber-400 font-semibold text-lg mb-4">
                  {instructor.title}
                </p>
              </div>
              
              <div className="space-y-4">
                {instructor.specialties.map((specialty, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <SafeIcon icon={FiAward} className="text-amber-400 text-lg flex-shrink-0 mt-0.5" />
                    <p className="text-slate-300 leading-relaxed text-sm">
                      {specialty}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl p-8 border border-amber-500/30 max-w-4xl mx-auto mb-8 backdrop-blur-sm">
            <div className="flex items-center justify-center gap-3 mb-6">
              <SafeIcon icon={FiTrendingUp} className="text-amber-400 text-3xl" />
              <h3 className="text-2xl font-bold text-amber-400">
                ความพิเศษของการรวมตัวครั้งนี้
              </h3>
              <SafeIcon icon={FiBookOpen} className="text-amber-400 text-3xl" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-6 border border-blue-500/20">
                <h4 className="text-lg font-bold text-blue-400 mb-3">
                  📊 มุมมองเชิงธุรกิจ
                </h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  อาจารย์รวิจะสอนให้คุณเข้าใจโครงสร้างดวงชะตาในมุมมองการลงทุน และการประยุกต์ใช้ในการตัดสินใจทางธุรกิจ
                </p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/20">
                <h4 className="text-lg font-bold text-purple-400 mb-3">
                  🔮 พิธีกรรมโบราณ
                </h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  นักพรตเฮอซานเฉิงจะสอนวิธีการเสริมดวงผ่านพิธีกรรมที่ถูกต้อง และการใช้ยันต์ประจำตัวอย่างมีประสิทธิภาพ
                </p>
              </div>
            </div>
            
            <p className="text-lg text-white leading-relaxed">
              นี่คือโอกาสเดียวที่คุณจะได้เรียนรู้จาก<span className="text-amber-400 font-semibold">ผู้เชี่ยวชาญทั้งสองสาขา</span>
              ในเวลาเดียวกัน ทำให้ได้ความรู้ที่<span className="text-green-400 font-semibold">ครบถ้วนและสมบูรณ์</span>
              มากกว่าการเรียนที่ใดๆ
            </p>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBooking}
            className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-10 py-4 rounded-full text-lg font-bold shadow-2xl hover:shadow-amber-500/25 transition-all duration-300"
          >
            เรียนจากผู้เชี่ยวชาญเดี๋ยวนี้
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default InstructorSection;